import { toUserFacingApiError } from "lib/utils/userFacingApiError";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import CollisionPolygonEditor from "components/control/settings/CollisionPolygonEditor";
import FunctionalButton from "components/layout/FunctionalButton";
import {
    fetchNav2OperatorParams,
    fetchNav2CollisionMonitor,
    getNav2ParamsApiBaseUrl,
    patchNav2CollisionMonitor,
    patchNav2OperatorParams,
    type Nav2CollisionMonitorResponse,
} from "lib/nav2ParamsApi";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import { useARC } from "providers/ARC/ARCProvider";
import { useToast } from "providers/ToastProvider";

const DEFAULT_VX = { min: 0.05, max: 4.0 };
const DEFAULT_COST = { min: 0.1, max: 10.0 };
const DEFAULT_RADIUS = { min: 0.05, max: 5.0 };

type RangeLimits = { min: number; max: number };

type SliderFieldProps = {
    label: string;
    hint: string;
    value: number;
    unit?: string;
    limits: RangeLimits;
    step: number;
    disabled: boolean;
    decimals?: number;
    className?: string;
    onChange: (value: number) => void;
};

const SliderField: React.FC<SliderFieldProps> = ({
    label,
    hint,
    value,
    unit,
    limits,
    step,
    disabled,
    decimals = 2,
    className = "mt-6",
    onChange,
}) => (
    <label className={`flex flex-col gap-2 ${className}`}>
        <div className="flex items-center justify-between">
            <span className="text-h5 font-semibold text-white">{label}</span>
            <span className="text-h5 text-black40">
                {value.toFixed(decimals)}
                {unit ? ` ${unit}` : ""}
            </span>
        </div>
        <p className="text-h5 text-black60">{hint}</p>
        <input
            type="range"
            min={limits.min}
            max={limits.max}
            step={step}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(Number(e.target.value))}
            className="w-full"
            aria-label={label}
        />
    </label>
);

const NavigationSettingsPanel: React.FC = () => {
    const { t } = useTranslation(["settings", "common"]);
    const { selectedARC } = useARC();
    const { addToast } = useToast();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();

    const baseUrl = useMemo(
        () => getNav2ParamsApiBaseUrl(selectedARC?.host ?? null, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [profileName, setProfileName] = useState<string | null>(null);
    const [vxMax, setVxMax] = useState(1.0);
    const [localCostScaling, setLocalCostScaling] = useState(2.5);
    const [globalCostScaling, setGlobalCostScaling] = useState(3.0);
    const [localInflationRadius, setLocalInflationRadius] = useState(1.5);
    const [globalInflationRadius, setGlobalInflationRadius] = useState(1.5);
    const [vxLimits, setVxLimits] = useState(DEFAULT_VX);
    const [costLimits, setCostLimits] = useState(DEFAULT_COST);
    const [radiusLimits, setRadiusLimits] = useState(DEFAULT_RADIUS);
    const [dirty, setDirty] = useState(false);
    const [collisionMonitorEnabled, setCollisionMonitorEnabled] = useState(true);
    const [collisionMonitorAvailable, setCollisionMonitorAvailable] = useState(true);
    const [collisionMonitor, setCollisionMonitor] =
        useState<Nav2CollisionMonitorResponse | null>(null);
    const [collisionSaving, setCollisionSaving] = useState(false);

    const refresh = useCallback(async () => {
        if (!baseUrl) {
            setError(null);
            setProfileName(null);
            setCollisionMonitorAvailable(true);
            setCollisionMonitor(null);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const operatorRes = await fetchNav2OperatorParams(baseUrl);
            setProfileName(operatorRes.profile_name ?? null);
            setVxMax(operatorRes.vx_max);
            setLocalCostScaling(
                operatorRes.local_cost_scaling_factor ?? operatorRes.cost_scaling_factor ?? 2.5
            );
            setGlobalCostScaling(
                operatorRes.global_cost_scaling_factor ??
                    operatorRes.local_cost_scaling_factor ??
                    operatorRes.cost_scaling_factor ??
                    3.0
            );
            setLocalInflationRadius(operatorRes.local_inflation_radius ?? 1.5);
            setGlobalInflationRadius(operatorRes.global_inflation_radius ?? 1.5);
            if (operatorRes.limits?.vx_max) setVxLimits(operatorRes.limits.vx_max);
            const nextCostLimits =
                operatorRes.limits?.local_cost_scaling_factor ??
                operatorRes.limits?.cost_scaling_factor;
            if (nextCostLimits) setCostLimits(nextCostLimits);
            const nextRadiusLimits =
                operatorRes.limits?.local_inflation_radius ??
                operatorRes.limits?.global_inflation_radius;
            if (nextRadiusLimits) setRadiusLimits(nextRadiusLimits);
            setDirty(false);
        } catch (e) {
            setError(toUserFacingApiError(e, t("navigation.loadFailed")));
            setProfileName(null);
            setCollisionMonitorAvailable(false);
            setCollisionMonitor(null);
            setLoading(false);
            return;
        }

        try {
            const collisionRes = await fetchNav2CollisionMonitor(baseUrl);
            setCollisionMonitorEnabled(Boolean(collisionRes.enabled));
            setCollisionMonitor(collisionRes);
            setCollisionMonitorAvailable(true);
        } catch {
            // Older nav_config_api builds may lack /nav2/collision-monitor.
            setCollisionMonitorAvailable(false);
            setCollisionMonitor(null);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, t]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const onSave = async () => {
        if (!baseUrl || saving) return;
        setSaving(true);
        try {
            const res = await patchNav2OperatorParams(baseUrl, {
                vx_max: vxMax,
                local_cost_scaling_factor: localCostScaling,
                global_cost_scaling_factor: globalCostScaling,
                local_inflation_radius: localInflationRadius,
                global_inflation_radius: globalInflationRadius,
            });
            setVxMax(res.vx_max);
            setLocalCostScaling(
                res.local_cost_scaling_factor ?? res.cost_scaling_factor ?? localCostScaling
            );
            setGlobalCostScaling(
                res.global_cost_scaling_factor ??
                    res.local_cost_scaling_factor ??
                    globalCostScaling
            );
            setLocalInflationRadius(res.local_inflation_radius ?? localInflationRadius);
            setGlobalInflationRadius(res.global_inflation_radius ?? globalInflationRadius);
            setProfileName(res.profile_name ?? profileName);
            setDirty(false);
            const liveFailed =
                Array.isArray(res.live_errors) && res.live_errors.length > 0;
            const liveOk = res.live_applied === true;
            addToast({
                type: liveFailed ? "warning" : "success",
                message: liveFailed
                    ? t("navigation.savedLivePartial", {
                          defaultValue:
                              "Navigation settings saved to profile. Some live updates failed; they will apply when navigation next starts.",
                      })
                    : liveOk
                      ? t("navigation.saved", {
                            defaultValue:
                                "Navigation settings saved and applied (no restart).",
                        })
                      : t("navigation.savedOffline", {
                            defaultValue:
                                "Navigation settings saved to profile. Start navigation to use them, or they will apply on the next start.",
                        }),
            });
        } catch (e) {
            addToast({
                type: "error",
                message: toUserFacingApiError(e, t("navigation.saveFailed")),
            });
        } finally {
            setSaving(false);
        }
    };

    const onToggleCollisionMonitor = async () => {
        if (!baseUrl || !collisionMonitorAvailable || loading || saving || collisionSaving) {
            return;
        }
        const nextEnabled = !collisionMonitorEnabled;
        setCollisionSaving(true);
        try {
            const res = await patchNav2CollisionMonitor(baseUrl, nextEnabled);
            setCollisionMonitorEnabled(res.enabled);
            setCollisionMonitor(res);
            addToast({
                type: "success",
                message: nextEnabled
                    ? t("navigation.collisionMonitorEnabled", {
                          defaultValue:
                              "Collision monitor enabled. Navigation is restarting.",
                      })
                    : t("navigation.collisionMonitorDisabled", {
                          defaultValue:
                              "Collision monitor disabled. Navigation is restarting.",
                      }),
            });
        } catch (e) {
            addToast({
                type: "error",
                message:
                    e instanceof Error
                        ? e.message
                        : t("navigation.collisionMonitorSaveFailed", {
                              defaultValue: "Could not update collision monitor state.",
                          }),
            });
        } finally {
            setCollisionSaving(false);
        }
    };

    const markDirty = (setter: (value: number) => void) => (value: number) => {
        setter(value);
        setDirty(true);
    };

    const controlsDisabled = !baseUrl || loading || saving || Boolean(error);
    const showPolygonEditor =
        Boolean(baseUrl) &&
        collisionMonitorAvailable &&
        collisionMonitor?.polygons &&
        (collisionMonitor.polygons.PolygonStop || collisionMonitor.polygons.PolygonSlow);

    return (
        <div className="flex max-w-3xl flex-col gap-4">
            <div>
                <p className="text-h4 font-semibold text-white">{t("navigation.title")}</p>
                <p className="mt-1 text-h5 text-black40">{t("navigation.description")}</p>
                {!baseUrl ? (
                    <p className="mt-2 text-h5 text-black60">{t("navigation.connectFirst")}</p>
                ) : null}
                {profileName ? (
                    <p className="mt-2 text-h5 text-black40">
                        {t("navigation.activeProfile", {
                            profile: profileName,
                            defaultValue: `Active robot profile: {{profile}}`,
                        })}
                    </p>
                ) : null}
                {error ? <p className="mt-2 text-h5 text-red-400">{error}</p> : null}
            </div>

            <div className="rounded-r10 border border-black80 bg-black90 p-4">
                {collisionMonitorAvailable ? (
                    <div className="mb-6 rounded-r5 border border-black80 bg-black80 p-3">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="text-h5 font-semibold text-white">
                                    {t("navigation.collisionMonitor", {
                                        defaultValue: "Collision monitor",
                                    })}
                                </p>
                                <p className="mt-1 text-h5 text-black40">
                                    {t("navigation.collisionMonitorHint", {
                                        defaultValue:
                                            "Enable or disable collision monitor safety filtering for navigation. If navigation is running, this change restarts navigation to apply.",
                                    })}
                                </p>
                            </div>
                            <FunctionalButton
                                title={
                                    collisionSaving
                                        ? t("common:saving")
                                        : collisionMonitorEnabled
                                          ? t("common:disable", { defaultValue: "Disable" })
                                          : t("common:enable", { defaultValue: "Enable" })
                                }
                                type="button"
                                className={`rounded-r5 border px-4 py-2 !normal-case text-h5 font-semibold text-white disabled:opacity-50 ${
                                    collisionMonitorEnabled
                                        ? "border-red-500 bg-red-500 hover:bg-red-400"
                                        : "border-primary2 bg-primary2 hover:bg-primary1"
                                }`}
                                disabled={controlsDisabled || collisionSaving}
                                onClick={() => void onToggleCollisionMonitor()}
                            />
                        </div>
                        <p className="mt-2 text-h5 text-black40">
                            {collisionMonitorEnabled
                                ? t("navigation.collisionMonitorStateOn", {
                                      defaultValue: "Current state: enabled",
                                  })
                                : t("navigation.collisionMonitorStateOff", {
                                      defaultValue: "Current state: disabled",
                                  })}
                        </p>
                        {showPolygonEditor && baseUrl ? (
                            <CollisionPolygonEditor
                                baseUrl={baseUrl}
                                disabled={
                                    controlsDisabled ||
                                    collisionSaving ||
                                    !collisionMonitorEnabled
                                }
                                collisionMonitor={collisionMonitor}
                                onUpdated={(res) => {
                                    setCollisionMonitor(res);
                                    setCollisionMonitorEnabled(Boolean(res.enabled));
                                }}
                            />
                        ) : null}
                    </div>
                ) : baseUrl && !error ? (
                    <div className="mb-6 rounded-r5 border border-black80 bg-black80 p-3">
                        <p className="text-h5 font-semibold text-white">
                            {t("navigation.collisionMonitor", {
                                defaultValue: "Collision monitor",
                            })}
                        </p>
                        <p className="mt-1 text-h5 text-black40">
                            {t("navigation.collisionMonitorUnavailable", {
                                defaultValue:
                                    "Collision monitor controls are not available on this ARC. Update nav_config_api to enable them.",
                            })}
                        </p>
                    </div>
                ) : null}

                <SliderField
                    label={t("navigation.speed")}
                    hint={t("navigation.speedHint")}
                    value={vxMax}
                    unit="m/s"
                    limits={vxLimits}
                    step={0.05}
                    disabled={controlsDisabled}
                    className=""
                    onChange={markDirty(setVxMax)}
                />

                <SliderField
                    label={t("navigation.localCostScaling")}
                    hint={t("navigation.localCostScalingHint")}
                    value={localCostScaling}
                    limits={costLimits}
                    step={0.1}
                    disabled={controlsDisabled}
                    onChange={markDirty(setLocalCostScaling)}
                />

                <SliderField
                    label={t("navigation.globalCostScaling")}
                    hint={t("navigation.globalCostScalingHint")}
                    value={globalCostScaling}
                    limits={costLimits}
                    step={0.1}
                    disabled={controlsDisabled}
                    onChange={markDirty(setGlobalCostScaling)}
                />

                <SliderField
                    label={t("navigation.localInflationRadius")}
                    hint={t("navigation.localInflationRadiusHint")}
                    value={localInflationRadius}
                    unit="m"
                    limits={radiusLimits}
                    step={0.05}
                    disabled={controlsDisabled}
                    onChange={markDirty(setLocalInflationRadius)}
                />

                <SliderField
                    label={t("navigation.globalInflationRadius")}
                    hint={t("navigation.globalInflationRadiusHint")}
                    value={globalInflationRadius}
                    unit="m"
                    limits={radiusLimits}
                    step={0.05}
                    disabled={controlsDisabled}
                    onChange={markDirty(setGlobalInflationRadius)}
                />

                <div className="mt-6 flex flex-wrap gap-3">
                    <FunctionalButton
                        title={saving ? t("common:saving") : t("common:save")}
                        type="button"
                        className="rounded-r5 border border-primary2 bg-primary2 px-4 py-2 !normal-case text-h5 font-semibold text-white hover:bg-primary1 disabled:opacity-50"
                        disabled={controlsDisabled || !dirty}
                        onClick={() => void onSave()}
                    />
                    <FunctionalButton
                        title={t("common:refresh", { defaultValue: "Refresh" })}
                        type="button"
                        className="rounded-r5 border border-black60 bg-black90 px-4 py-2 !normal-case text-h5 font-semibold text-white hover:border-black40 disabled:opacity-50"
                        disabled={!baseUrl || loading || saving}
                        onClick={() => void refresh()}
                    />
                </div>
            </div>
        </div>
    );
};

export default NavigationSettingsPanel;
